import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { EChartsOption } from 'echarts';
import {
  CHART_TYPE,
  ERROR_COLORS,
} from 'src/app/modules/campaign-scheduler/constants/schedule-campaign.constant';

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.scss'],
})
export class ChartsComponent implements OnInit, OnChanges {
  @Input() chartType: string = CHART_TYPE.BAR;
  @Input() width: string = '100%';
  @Input() height: string = '400px';
  @Input() border: boolean = true;
  @Input() isLegends: boolean = true;
  @Input() isTooltip: boolean = true;
  @Input() showDataLabels: boolean = true;
  @Input() showGrid: boolean = true;
  @Input() colors: string[] = [
    '#2762C1',
    '#FA5959',
    '#55C3B9',
    '#FFA726',
    '#9B59B6',
    '#FF1A1A',
  ];
  @Input() title: string = '';
  @Input() legendPosition: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  @Input() barWidth: string = '40%';
  @Input() stackName: string = 'campaign';
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';

  @Input() metrics: string[];
  @Input() metricValues: number[] = [];
  @Input() stackedData: number[][] = [];
  @Input() categories: string[] = [];
  @Input() heatmapData: any[] = [];
  @Input() heatmapXAxisData: string[] = [];
  @Input() heatmapYAxisData: string[] = [];
  @Input() doubleDonutData: any[] = [];
  @Input() multiLineData: any[] = [];
  @Input() hideZeroInTooltip: boolean = false;
  chartOption: EChartsOption = {};

  ngOnInit(): void {
    this.initChart();
  }

  ngOnChanges(): void {
    this.initChart();
  }

  initChart(): void {
    if (
      this.metricValues.length > 0 ||
      this.stackedData.length > 0 ||
      this.heatmapData.length > 0 ||
      this.doubleDonutData.length > 0 ||
      this.multiLineData.length > 0
    ) {
      this.chartOption = this.createChartFromMetrics();
    }
  }

  createChartFromMetrics(): EChartsOption {
    switch (this.chartType) {
      case CHART_TYPE.BAR:
        return this.createBarChartFromMetrics();
      case CHART_TYPE.DONUT:
        return this.createPieChartFromMetrics();
      case CHART_TYPE.LOLLIPOP:
        return this.createLollipopChartFromMetrics();
      case CHART_TYPE.STACKED_BAR:
        return this.createStackedBarChartFromMetrics();
      case CHART_TYPE.HEATMAP:
        return this.createHeatmapChartFromMetrics();
      case CHART_TYPE.DOUBLE_DONUT:
        return this.createDoubleDonutChartFromMetrics();
      case CHART_TYPE.LINE:
        return this.prepareLineChart();
      case CHART_TYPE.MULTI_LINE:
        return this.prepareMultiLineChart(
          this.categories,
          this.metrics,
          this.multiLineData
        );
      default:
        return {};
    }
  }

  createDoubleDonutChartFromMetrics(): EChartsOption {
    return {
      title: {
        text: this.title,
        left: 'center',
        top: 'middle',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          align: 'center',
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}',
      },
      series: {
        type: 'sunburst',
        nodeClick: false,
        radius: ['55%', '80%'],
        center: ['50%', '50%'],
        data: this.doubleDonutData,
        label: {
          show: true,
        },
        levels: [
          {},
          {
            r0: '30%',
            r: '55%',
            label: {
              position: 'inside',
              rotate: this.doubleDonutData.length > 1 ? 'radial' : 'tangential',
              overflow: 'break',
              fontWeight: 'bold',
              color: '#000',
              formatter: function (node: any) {
                return `${node?.data?.name}`;
              },
            },
          },
          {
            r0: '55%',
            r: '80%',
            label: {
              position: 'outside',
              overflow: 'break',
              fontWeight: 'bold',
              padding: 3,
              silent: false,
              rotate: this.doubleDonutData.length > 1 ? 'radial' : 'tangential',
              color: '#000',
              formatter: function (node: any) {
                const name = node?.data?.name || '';
                const shortName =
                  name.length > 15 ? name.slice(0, 15) + '...' : name;
                return `${shortName}`;
              },
            },
            itemStyle: {
              borderWidth: 1,
            },
          },
        ],
      },
    };
  }

  createBarChartFromMetrics(): EChartsOption {
    return this.buildBarChart(this.metrics, this.metricValues);
  }

  createPieChartFromMetrics(): EChartsOption {
    return this.buildPieChart(this.metrics, this.metricValues);
  }

  createLollipopChartFromMetrics(): EChartsOption {
    return this.buildLollipopChart(this.metrics, this.metricValues);
  }

  createStackedBarChartFromMetrics(): EChartsOption {
    const dataToUse =
      this.stackedData.length > 0 ? this.stackedData : this.metricValues;
    const categoriesToUse =
      this.categories.length > 0 ? this.categories : this.metrics;
    return this.buildStackedBarChart(categoriesToUse, dataToUse);
  }

  createHeatmapChartFromMetrics(): EChartsOption {
    return this.buildHeatmapChart(
      this.heatmapXAxisData,
      this.heatmapYAxisData,
      this.heatmapData
    );
  }

  buildBarChart(labels: string[], values: number[]): EChartsOption {
    const maxValue = values.length > 0 ? Math.max(...values) : 0;
    const blockSize =
      maxValue > 0 ? Math.pow(10, Math.floor(Math.log10(maxValue))) : 10;
    const yAxisMax = Math.ceil(maxValue / blockSize) * blockSize + blockSize;

    return {
      title: this.title
        ? {
            text: this.title,
            left: 'center',
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
            },
          }
        : undefined,
      tooltip: this.isTooltip
        ? {
            trigger: 'item',
            formatter: '{b}: {c}',
            axisPointer: {
              type: 'none',
            },
          }
        : undefined,
      legend: this.isLegends
        ? {
            show: true,
            [this.legendPosition]:
              this.legendPosition === 'top' || this.legendPosition === 'bottom'
                ? 10
                : 'center',
            left:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? this.legendPosition
                : 'center',
            orient:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? 'vertical'
                : 'horizontal',
            itemGap: 20,
            data: labels,
            textStyle: {
              fontSize: 12,
              rich: {
                a: {
                  verticalAlign: 'bottom',
                  lineHeight: 16,
                },
              },
            },
            formatter: (name: string) => `{a|${name}}`,
            icon: 'rect',
            itemWidth: 14,
            itemHeight: 14,
          }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        bottom:
          this.isLegends && this.legendPosition === 'bottom' ? '15%' : '10%',
        top: this.isLegends && this.legendPosition === 'top' ? '15%' : '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: this.showGrid,
          lineStyle: {
            color: '#B0B0B0',
            type: 'solid',
            width: 1,
          },
        },
      },
      yAxis: {
        type: 'value',
        name: this.yAxisLabel,
        nameLocation: 'middle',
        nameGap: 60,
        max: yAxisMax,
        splitLine: {
          show: this.showGrid,
          lineStyle: {
            color: '#B0B0B0',
          },
        },
      },
      series: labels.map((label, index) => ({
        name: label,
        type: CHART_TYPE.BAR as 'bar',
        stack: this.stackName,
        data: values.map((val, i) => (i === index ? (val === 0 ? '-' : val) : '-')),
        itemStyle: {
          color: this.colors[index % this.colors.length],
          borderRadius: [4, 4, 0, 0],
        },
        barWidth: this.barWidth,
        barMinHeight: 5,
        label: this.showDataLabels
          ? {
              show: true,
              position: 'top',
              color: '#333',
              fontSize: 14,
              fontWeight: 'bold',
              textBorderColor: '#fff',
              textBorderWidth: 2,
              formatter: function (params: any) {
                return params.value > 0 ? params.value : '';
              },
            }
          : undefined,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      })),
    };
  }

  buildPieChart(labels: string[], values: number[]): EChartsOption {
    const totalValue = values.reduce((sum, val) => sum + val, 0);

    return {
      title: this.title
        ? {
            text: this.title,
            left: 'center',
            top: '5%',
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
            },
          }
        : undefined,
      tooltip: this.isTooltip
        ? {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)',
          }
        : undefined,
      legend: this.isLegends
        ? {
            type: 'scroll',
            orient:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? 'vertical'
                : 'horizontal',
            [this.legendPosition]: '0%',
            data: labels.map((label: string) => ({
              name: label,
              icon: 'square',
            })),
            textStyle: {
              fontSize: 12,
              rich: {
                a: {
                  verticalAlign: 'bottom',
                  lineHeight: 16,
                },
              },
            },
            formatter: (name: string) => `{a|${name}}`,
            icon: 'rect',
            itemWidth: 14,
            itemHeight: 14,
            itemGap: 20,
          }
        : undefined,
      graphic: this.showDataLabels
        ? [
            {
              type: 'text',
              left: 'center',
              top: 'middle',
              style: {
                text: `Total`,
                fill: '#999',
                align: 'center',
                lineHeight: 20,
                fontSize: 16,
                fontWeight: 'bold',
              },
            },
            {
              type: 'text',
              left: 'center',
              top: '52%',
              style: {
                text: `${totalValue}`,
                align: 'center',
                fill: '#666',
                fontSize: 16,
                fontWeight: 'bold',
              },
            },
          ]
        : undefined,
      series: [
        {
          name: this.title || 'Data Breakdown',
          type: 'pie',
          radius: this.chartType === CHART_TYPE.DONUT ? ['40%', '70%'] : '70%',
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: this.showDataLabels
            ? {
                show: true,
                position: 'outside',
                formatter: '{c}',
                fontSize: 14,
              }
            : { show: false },
          labelLine: this.showDataLabels
            ? {
                show: true,
                length: 30,
                length2: 20,
              }
            : { show: false },
          data: labels.map((label: string, index: number) => ({
            value: values[index] || 0,
            name: label,
            itemStyle: { color: ERROR_COLORS[index % ERROR_COLORS.length] },
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
  }

  buildLollipopChart(labels: string[], values: number[]): EChartsOption {
    return {
      animation: false,
      title: this.title
        ? {
            text: this.title,
            left: 'left',
            top: '2%',
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
            },
          }
        : undefined,
      tooltip: this.isTooltip
        ? {
            trigger: 'axis',
            formatter: function (params: any) {
              const dataPoint = params[0];
              return `${dataPoint.name}: ${dataPoint.value}`;
            },
            axisPointer: {
              type: 'none',
            },
          }
        : undefined,
      grid: {
        left: '5%',
        right: '10%',
        top: '12%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: undefined,
        name: this.xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 12,
          fontWeight: 'normal',
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        axisLabel: {
          show: true,
          fontSize: 11,
          opacity: 1,
          margin: 8,
          formatter: function (value: number) {
            if (value % 1 !== 0) {
              return '';
            }
            const absValue = Math.round(value);
            if (absValue >= 1000) {
              return (absValue / 1000).toFixed(1).replace('.0', '') + 'k';
            }
            return absValue.toString();
          },
        },
        splitLine: {
          show: this.showGrid,
          lineStyle: {
            color: '#B0B0B0',
            type: 'solid',
            width: 1,
            opacity: 1,
          },
        },
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          fontSize: 11,
          margin: 8,
          opacity: 1,
        },
        inverse: true,
      },
      series: [
        {
          name: 'Clicks',
          type: CHART_TYPE.BAR as 'bar',
          data: values,
          itemStyle: {
            color: 'transparent',
            borderColor: this.colors[0] || '#2762C1',
            borderWidth: 5,
            borderType: 'solid',
            opacity: 1,
          },
          barWidth: 5,
          barCategoryGap: '20%',
          label: this.showDataLabels
            ? {
                show: true,
                position: 'right',
                color: this.colors[0] || '#2762C1',
                fontSize: 14,
                fontWeight: 'bold',
                distance: 15,
                opacity: 1,
                formatter: function (params: any) {
                  return params.value;
                },
              }
            : undefined,
          emphasis: {
            disabled: true,
          },
        },
        {
          name: 'Values',
          type: 'scatter',
          data: values.map((value, index) => [value, index]),
          symbolSize: 22,
          itemStyle: {
            color: this.colors[0] || '#2762C1',
            borderColor: '#ffffff',
            borderWidth: 2,
            shadowBlur: 0,
            shadowColor: 'transparent',
            opacity: 1,
          },
          z: 10,
          emphasis: {
            disabled: true,
          },
        },
      ],
    };
  }

  buildStackedBarChart(
    categories: string[],
    dataValues: number[] | number[][]
  ): EChartsOption {
    let processedData: number[][];
    if (Array.isArray(dataValues[0])) {
      processedData = dataValues as number[][];
    } else {
      processedData = (dataValues as number[]).map((value, index) =>
        categories.map(() => (index === 0 ? value : 0))
      );
    }

    return {
      title: this.title
        ? {
            text: this.title,
            left: 'center',
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
            },
          }
        : undefined,
      tooltip: this.isTooltip
        ? {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
            formatter: (params: any) => {
              let result = `${params[0].name}<br/>`;
              params.forEach((param: any) => {
                // Skip series with zero values if hideZeroInTooltip is enabled
                if (!this.hideZeroInTooltip || param.value > 0) {
                  result += `${param.marker}${param.seriesName}: ${param.value}<br/>`;
                }
              });
              return result;
            },
          }
        : undefined,
      legend: this.isLegends
        ? {
            show: true,
            [this.legendPosition]:
              this.legendPosition === 'top' || this.legendPosition === 'bottom'
                ? 10
                : 'center',
            left:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? this.legendPosition
                : 'center',
            orient:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? 'vertical'
                : 'horizontal',
            itemGap: 20,
            textStyle: {
              fontSize: 12,
              rich: {
                a: {
                  verticalAlign: 'bottom',
                  lineHeight: 16,
                },
              },
            },
            formatter: (name: string) => `{a|${name}}`,
            icon: 'rect',
            itemWidth: 14,
            itemHeight: 14,
          }
        : undefined,
      grid: {
        left: '6%',
        right: '4%',
        bottom:
          this.isLegends && this.legendPosition === 'bottom' ? '15%' : '10%',
        top: this.isLegends && this.legendPosition === 'top' ? '15%' : '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          rotate: 45,
          fontSize: 10,
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
      },
      yAxis: {
        type: 'value',
        name: this.yAxisLabel,
        nameLocation: 'middle',
        nameGap: 60,
        max: undefined,
        axisLabel: {
          fontSize: 11,
        },
        splitLine: {
          show: this.showGrid,
          lineStyle: {
            color: '#B0B0B0',
            type: 'solid',
          },
        },
      },
      series: this.metrics?.map((seriesName, index) => ({
        name: seriesName,
        type: CHART_TYPE.BAR as 'bar',
        stack: this.stackName,
        data: (processedData[index] || []).map((val) => (val === 0 ? '-' : val)),
        itemStyle: {
          color: this.colors[index % this.colors.length],
        },
        barWidth: this.barWidth,
        barMinHeight: 5,
        label: this.showDataLabels
          ? {
              show: true,
              position: 'inside',
              color: 'white',
              fontSize: 12,
              fontWeight: 'bold',
              textBorderColor: 'black',
              textBorderWidth: 1,
              formatter: function (params: any) {
                return params.value > 0 ? params.value : '';
              },
            }
          : undefined,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      })),
    };
  }

  buildHeatmapChart(
    xAxisData: string[],
    yAxisData: string[],
    data: any[]
  ): EChartsOption {
    const values = data.map((item) => item[2]);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    return {
      title: this.title
        ? {
            text: this.title,
            left: 'center',
            top: '2%',
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold',
            },
          }
        : undefined,
      tooltip: this.isTooltip
        ? {
            trigger: 'item',
            formatter: function (params: any) {
              const xLabel = xAxisData[params.data[0]] ?? 'NA';
              const yLabel = yAxisData[params.data[1]] ?? 'NA';
              const value = params.data[2];
              return `${yLabel}<br/>${xLabel}: <strong>${value}</strong>`;
            },
          }
        : undefined,
      grid: {
        height: '70%',
        top: this.title ? '15%' : '10%',
        left: '10%',
        right: '10%',
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        splitArea: {
          show: true,
        },
        axisLabel: {
          fontSize: 10,
          rotate: 0,
          margin: 8,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        position: 'bottom',
      },
      yAxis: {
        type: 'category',
        data: yAxisData,
        splitArea: {
          show: true,
        },
        axisLabel: {
          formatter: (value: any) => `${value.split(' ').join('\n')}`,
          fontSize: 11,
          margin: 8,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        inverse: true,
      },
      visualMap: {
        min: minValue,
        max: maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        inRange: {
          color: this.colors,
        },
        text: ['High', 'Low'],
        textStyle: {
          fontSize: 12,
        },
        itemWidth: 30,
        itemHeight: 900,
      },
      series: [
        {
          name: 'Clicks',
          type: CHART_TYPE.HEATMAP as 'heatmap',
          data: data,
          label: this.showDataLabels
            ? {
                show: true,
                fontSize: 14,
                fontWeight: 'bold',
                color: '#000',
                formatter: function (params: any) {
                  return params.data[2];
                },
              }
            : { show: false },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
        },
      ],
    };
  }

  prepareLineChart(): EChartsOption {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          return `${data.name}: ₹ ${data.value.toLocaleString()}`;
        },
      },
      xAxis: {
        type: 'category',
        data: this.categories,
        axisLabel: {
          interval: 0,
          margin: 20,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 100000) {
              return `₹ ${(value / 100000).toFixed(1)} Lakh`;
            }
            return `₹ ${value.toLocaleString()}`;
          },
        },
        splitLine: {
          show: true,
          // lineStyle: {
          //   type: 'solid',
          //   color: 'grey',
          // },
        },
      },
      series: [
        {
          data: this.metricValues,
          type: 'line',
          smooth: false,
          lineStyle: {
            color: '#a78bfa',
            width: 3,
          },
          itemStyle: {
            color: '#a78bfa',
            borderWidth: 3,
            borderColor: '#a78bfa',
          },
          symbolSize: 16,
          label: {
            show: true,
            formatter: (params: any) => `₹ ${params.value.toLocaleString()}`,
            position: 'top',
            fontWeight: 'bold',
          },
          areaStyle: {
            opacity: 0.1,
            color: '#a78bfa',
          },
        },
      ],
      grid: {
        left: '8%',
        right: '8%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
    };
  }

  prepareMultiLineChart(
    categories: string[],
    metrics: string[],
    data: any[]
  ): EChartsOption {
    const chartOptions: EChartsOption = {
      color: this.colors,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          let tooltipText = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((item: any) => {
            tooltipText += `
          <span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${item.color};"></span>
          ${item.seriesName}: <b>₹ ${item.data}</b><br/>
        `;
          });
          return tooltipText;
        },
      },
      legend: this.isLegends
        ? {
            show: true,
            [this.legendPosition]:
              this.legendPosition === 'top' || this.legendPosition === 'bottom'
                ? 10
                : 'center',
            left:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? this.legendPosition
                : 'center',
            orient:
              this.legendPosition === 'left' || this.legendPosition === 'right'
                ? 'vertical'
                : 'horizontal',
            itemGap: 20,
            data: categories,
            textStyle: {
              fontSize: 12,
              rich: {
                a: {
                  verticalAlign: 'bottom',
                  lineHeight: 16,
                },
              },
            },
            formatter: (name: string) => `{a|${name}}`,
            icon: 'rect',
            itemWidth: 14,
            itemHeight: 14,
          }
        : undefined,
      xAxis: {
        type: 'category',
        data: metrics,
        axisLabel: {
          margin: 20,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 100000) {
              return `₹ ${(value / 100000).toFixed(1)} Lakh`;
            }
            return `₹ ${value.toLocaleString()}`;
          },
        },
      },
      series: data.map((seriesData: any, index: number) => ({
        ...seriesData,
        symbolSize: 16,
        // label: {
        //   show: true,
        //   formatter: (params: any) => `₹ ${params.value.toLocaleString()}`,
        //   position: 'top',
        //   fontWeight: 'bold',
        // },
      })),
    };

    return chartOptions;
  }
}
